import { ModalV2, AlertV2, ButtonV2 } from '@cimplico/components';
import { useState } from 'react';
import { toastNotification } from '../../../../components/toast/Toast';
import { useUpcomingInvoice } from '../../../../queries/billing/use-upcoming-invoice';
import { useUpdateSubscriptionV2 } from '../../../../queries/use-update-subscriptionv2';
import { SubscriptionV2 } from '../../../../types/firm';

type ConfirmChangesProps = {
    onClose: () => void;
    activeEntities?: number;
    interval?: string;
    subscription: SubscriptionV2;
};

function ConfirmPlanChanges({ onClose, activeEntities, interval, subscription }: ConfirmChangesProps): JSX.Element {
    const [err, setErr] = useState('');
    const isTrialing = subscription.status === 'TRIALING';
    const upcomingInvoice = useUpcomingInvoice(activeEntities, interval);
    const updateQuery = useUpdateSubscriptionV2();

    function isDowngrade(): boolean {
        if (isTrialing) {
            return false;
        }
        if (activeEntities) {
            if (activeEntities < subscription.totalActiveEntityAllocation) {
                return true;
            }
        }

        if (interval) {
            if (interval === 'MONTHLY' && subscription.interval === 'YEARLY') {
                return true;
            }
        }
        return false;
    }

    function update(): void {
        updateQuery.mutate(
            {
                activeEntities: activeEntities !== subscription.totalActiveEntityAllocation ? activeEntities : undefined,
                interval: interval !== subscription.interval ? interval : undefined,
            },
            {
                onSuccess: () => {
                    toastNotification('success', 'Subscription updated successfully');
                    onClose();
                },
                onError: (err) => {
                    setErr(err.response?.data.errors[0].detail);
                    toastNotification('error', 'Failed to update subscription');
                },
            }
        );
    }

    return (
        <ModalV2 isOpen onClose={onClose}>
            <ModalV2.Body>
                {err && <AlertV2 type="error" title="Unable to update subscription" description={[err]} className="mb-2" />}
                <div className="text-left mb-4">
                    <p className="text-sm text-contentPrimary font-bold">Confirm changes</p>
                    <p className="text-sm text-contentSecondary">
                        {isDowngrade()
                            ? "Your plan change is scheduled for the end of your billing period. Around 3 days prior, we'll check if your account fits within the limits of the new plan. If it doesn't, you'll receive a warning email. The downgrade will be cancelled if you're still over the limit at the time of change."
                            : isTrialing
                            ? "Your changes will apply immediately. You'll be charged accordingly when your subscription becomes active."
                            : 'You will be immediately charged the following amount:'}
                    </p>
                </div>
                {!isTrialing && !isDowngrade() && (
                    <div className="p-4 border rounded-xl">
                        <div className="flex justify-between">
                            <p className="text-sm font-bold text-contentPrimary">Subtotal</p>
                            <p className="text-sm text-contentPrimary">
                                {new Intl.NumberFormat('en-US', {
                                    style: 'currency',
                                    currency: 'aud',
                                })
                                    .format((upcomingInvoice?.data?.invoice?.subtotal ?? 0) / 100)
                                    .substring(1)}
                            </p>
                        </div>
                        <div className="flex justify-between mt-2">
                            <p className="text-sm font-bold text-contentPrimary">{upcomingInvoice?.data?.invoice?.tax.name}</p>
                            <p className="text-sm text-contentPrimary">
                                {new Intl.NumberFormat('en-US', {
                                    style: 'currency',
                                    currency: 'aud',
                                })
                                    .format((upcomingInvoice?.data?.invoice?.tax.amount ?? 0) / 100)
                                    .substring(1)}
                            </p>
                        </div>
                        <div className="flex justify-between border-t mt-2 pt-2">
                            <p className="text-sm font-bold text-contentPrimary">Total</p>
                            <p className="text-sm font-bold text-contentPrimary">
                                {new Intl.NumberFormat('en-US', {
                                    style: 'currency',
                                    currency: 'aud',
                                })
                                    .format((upcomingInvoice?.data?.invoice?.total ?? 0) / 100)
                                    .substring(1)}
                            </p>
                        </div>
                    </div>
                )}
            </ModalV2.Body>
            <ModalV2.Footer>
                <div className="flex justify-end gap-2">
                    <ButtonV2 size="sm" type="secondary" label="Cancel" onClick={onClose} />
                    <ButtonV2
                        size="sm"
                        label="Confirm"
                        isDisabled={upcomingInvoice.isLoading || updateQuery.isLoading}
                        isLoading={upcomingInvoice.isLoading || updateQuery.isLoading}
                        onClick={update}
                    />
                </div>
            </ModalV2.Footer>
        </ModalV2>
    );
}

export default ConfirmPlanChanges;
